import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home, CloudSun, LayoutDashboard, MapPin, Calculator, Wrench,
  Camera, Sparkles, Repeat, Package, ClipboardList, CalendarRange, Flame, ImageOff,
  DatabaseBackup, Archive,
} from "lucide-react";
import { FEATURE_LIST } from "@/lib/landing";

// Inline beaker (lucide paths) — avoids a barrel-resolution quirk with newly-added named imports.
const Beaker = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M4.5 3h15" />
    <path d="M6 3v16a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V3" />
    <path d="M6 14h12" />
  </svg>
);

const Trophy = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
    <path d="M4 22h16" />
    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
  </svg>
);

const ShieldCheck = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

const Sprout = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M7 20h10" />
    <path d="M10 20c5.5-2.5.8-6.4 3-10" />
    <path d="M9.5 9.4c1.1.8 1.8 2.2 2.3 3.7-2 .4-3.5.4-4.8-.3-1.2-.6-2.3-1.9-3-4.2 2.8-.5 4.4 0 5.5.8z" />
    <path d="M14.1 6a7 7 0 0 0-1.1 4c1.9-.1 3.3-.6 4.3-1.4 1-1 1.6-2.3 1.7-4.6-2.7.1-4 1-4.9 2z" />
  </svg>
);

const TreePine = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="m17 14 3 3.3a1 1 0 0 1-.7 1.7H4.7a1 1 0 0 1-.7-1.7L7 14h-.3a1 1 0 0 1-.7-1.7L9 9h-.2A1 1 0 0 1 8 7.3L12 3l4 4.3a1 1 0 0 1-.8 1.7H15l3 3.3a1 1 0 0 1-.7 1.7Z" />
    <path d="M12 22v-3" />
  </svg>
);

const ICONS = {
  Home, CloudSun, LayoutDashboard, MapPin, Calculator, Wrench,
  Camera, Sparkles, Repeat, Package, ClipboardList, CalendarRange, Flame,
  DatabaseBackup, Archive, Beaker, Trophy, ShieldCheck, Sprout, TreePine,
};

// Auto-rotating slideshow, one image at a time.
function ImageCarousel({ images, title }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((p) => (p + 1) % images.length), 3500);
    return () => clearInterval(t);
  }, [images.length]);
  return (
    <div className="relative w-full bg-[var(--ld-surface-2)]" style={{ aspectRatio: "1080 / 2520" }}>
      <AnimatePresence mode="wait">
        <motion.img
          key={i}
          src={images[i]}
          alt={`${title} ${i + 1}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: "easeInOut" }}
          className="absolute inset-0 h-full w-full object-cover object-top"
          loading="lazy"
        />
      </AnimatePresence>
      <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5">
        {images.map((_, d) => (
          <span
            key={d}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              d === i ? "w-5 bg-white" : "w-1.5 bg-white/50"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

// Lazy-loads (preload="none") and only plays while on screen; pauses when scrolled away.
function LazyVideo({ feature }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.play?.().catch(() => {});
          } else {
            el.pause?.();
          }
        });
      },
      { threshold: 0.25, rootMargin: "150px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <video
      ref={ref}
      muted
      loop
      playsInline
      preload="none"
      poster={feature.poster}
      className="block w-full object-cover"
      style={{ aspectRatio: "1080 / 2520" }}
    >
      {feature.videoWebm && <source src={feature.videoWebm} type="video/webm" />}
      <source src={feature.video} type="video/mp4" />
    </video>
  );
}

function PhoneFrame({ feature, Icon }) {
  return (
    <div className="relative w-[260px] overflow-hidden rounded-[2.4rem] border-[7px] border-[var(--ld-surface-2)] bg-[var(--ld-surface-2)] shadow-[0_30px_80px_rgba(0,0,0,0.55)] sm:w-[300px]">
      {feature.video ? (
        <LazyVideo feature={feature} />
      ) : feature.images ? (
        <ImageCarousel images={feature.images} title={feature.title} />
      ) : feature.img ? (
        <img src={feature.img} alt={`${feature.title} screen`} className="block w-full" loading="lazy" />
      ) : (
        <div className="flex aspect-[9/19.5] flex-col items-center justify-center gap-4 bg-gradient-to-b from-[var(--ld-surface)] to-[var(--ld-bg)] p-8 text-center">
          <div className="grid h-16 w-16 place-items-center rounded-2xl bg-[var(--ld-lime)]/12 text-[var(--ld-lime)]">
            <Icon className="h-8 w-8" />
          </div>
          <div className="flex items-center gap-1.5 text-xs font-black uppercase tracking-widest text-[var(--ld-muted)]">
            <ImageOff className="h-3.5 w-3.5" /> Screenshot coming
          </div>
          <div className="ld-display text-2xl text-[var(--ld-text)]/70">{feature.title}</div>
        </div>
      )}
    </div>
  );
}

export default function FeatureShowcase() {
  return (
    <section className="relative z-10 overflow-hidden py-20">
      <div className="pointer-events-none absolute left-1/2 top-0 h-[500px] w-[900px] -translate-x-1/2 rounded-full bg-[var(--ld-lime)]/8 blur-[140px]" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-20 max-w-3xl"
        >
          <span className="text-xs font-black uppercase tracking-[0.25em] text-[var(--ld-lime)]">
            A Tour of the App
          </span>
          <h2 className="ld-display mt-4 text-[clamp(2rem,5vw,3.75rem)]">
            Every section, up close.
          </h2>
        </motion.div>

        <div className="space-y-24 sm:space-y-32">
          {FEATURE_LIST.map((f, i) => {
            const Icon = ICONS[f.icon];
            const reverse = i % 2 === 1;
            return (
              <div
                key={f.id}
                id={`feat-${f.id}`}
                data-testid={`showcase-${f.id}`}
                className={`scroll-mt-24 grid items-center gap-10 lg:grid-cols-2 lg:gap-16 ${reverse ? "lg:[direction:rtl]" : ""}`}
              >
                <motion.div
                  initial={{ opacity: 0, x: reverse ? 60 : -60 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                  className="[direction:ltr]"
                >
                  <div className="flex items-center gap-3">
                    <span className="ld-display text-5xl text-[var(--ld-lime)]/30">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="grid h-11 w-11 place-items-center rounded-xl bg-[var(--ld-lime)]/12 text-[var(--ld-lime)]">
                      <Icon className="h-5 w-5" />
                    </span>
                  </div>
                  <div className="mt-4 text-sm font-black uppercase tracking-[0.2em] text-[var(--ld-lime)]">
                    {f.tagline}
                  </div>
                  <h3 className="mt-2 text-3xl font-black text-[var(--ld-text)] sm:text-4xl">
                    {f.title}
                  </h3>
                  <p className="mt-4 max-w-md text-lg leading-relaxed text-[var(--ld-muted)]">
                    {f.body}
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 50, rotate: reverse ? 3 : -3 }}
                  whileInView={{ opacity: 1, y: 0, rotate: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                  className="flex justify-center [direction:ltr]"
                >
                  <PhoneFrame feature={f} Icon={Icon} />
                </motion.div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
